import { useState } from 'react'
import { Container } from '../../components/layout/Container'
import { Card } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { knowledgeApi } from '../../lib/api'
import { Search, FileText, ArrowRight } from 'lucide-react'
import type { SearchResult } from '../../types'

export function SearchPage() {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<SearchResult[]>([])
  const [loading, setLoading] = useState(false)

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!query.trim()) return

    setLoading(true)
    try {
      const data = await knowledgeApi.search({ query, topK: 5 })
      setResults(data)
    } catch {
      setResults([])
    } finally {
      setLoading(false)
    }
  }

  return (
    <Container size="narrow" className="py-8">
      <div className="mb-8 text-center">
        <h1 className="text-2xl font-semibold text-text-primary">Búsqueda semántica</h1>
        <p className="mt-2 text-text-secondary">
          Pregunta en lenguaje natural. El sistema busca en la base de conocimientos usando vectores.
        </p>
      </div>

      <form onSubmit={handleSearch} className="mb-8">
        <div className="flex gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-text-muted" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Ej: ¿Cuál es la política de trabajo híbrido?"
              className="w-full rounded-xl border border-border bg-surface py-3 pl-10 pr-4 text-text-primary placeholder:text-text-muted focus:border-accent focus:outline-none"
            />
          </div>
          <Button type="submit" disabled={loading}>
            {loading ? 'Buscando...' : 'Buscar'}
          </Button>
        </div>
      </form>

      <div className="space-y-4">
        {results.map((result) => (
          <Card key={result.chunkId} variant="elevated">
            <div className="flex items-start gap-4">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-accent/10">
                <FileText className="h-5 w-5 text-accent-light" />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-text-primary">{result.originalName}</p>
                  <span className="text-xs text-text-muted">
                    Score: {(result.score * 100).toFixed(1)}%
                  </span>
                </div>
                <p className="mt-2 text-sm leading-relaxed text-text-secondary">
                  {result.contentText}
                </p>
                <div className="mt-3 flex items-center gap-1 text-xs text-text-muted">
                  <span className="font-mono">{result.localFilePath}</span>
                  <ArrowRight className="h-3 w-3" />
                </div>
              </div>
            </div>
          </Card>
        ))}

        {!loading && results.length === 0 && query && (
          <p className="text-center text-text-muted">No se encontraron resultados.</p>
        )}
      </div>
    </Container>
  )
}
